import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Star, TrendingUp, BookOpen, Zap, Crown, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  sparkles: Sparkles,
  star: Star,
  'trending-up': TrendingUp,
  'book-open': BookOpen,
  zap: Zap,
  crown: Crown,
};

/**
 * Public shareable badge card. Renders at /badge/:shareId — no auth required.
 * Shows only first name + badge (no PII).
 */
export default function PublicBadge() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/badges/share/${shareId}`)
      .then(res => setData(res.data))
      .catch(() => setError('This badge link is invalid or has expired.'));
  }, [shareId]);

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: `${data.student_first_name}'s ${data.badge.name}`, url: window.location.href })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied!');
    }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-6">
      <div className="text-center">
        <p className="text-lg font-bold">{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );

  const Icon = ICON_MAP[data.badge.icon] || Star;
  return (
    <div className="min-h-screen flex items-center justify-center px-4"
         style={{ background: 'radial-gradient(circle at top, #4f46e5 0%, #0f172a 60%)' }}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="public-badge-card">
        <div className={`bg-gradient-to-br ${data.badge.gradient} p-10 text-white text-center`}>
          <div className="w-24 h-24 mx-auto rounded-full bg-white/25 backdrop-blur border-4 border-white/40 flex items-center justify-center">
            <Icon className="w-12 h-12" />
          </div>
          <p className="mt-4 text-xs font-extrabold uppercase tracking-widest opacity-90">CodeITT Academy</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight" data-testid="badge-name">{data.badge.name}</h1>
        </div>
        <div className="p-6 text-center">
          <p className="text-lg font-extrabold text-slate-900" data-testid="badge-owner">
            Earned by <span className="text-indigo-600">{data.student_first_name}</span>
          </p>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{data.badge.description}</p>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Earned on {(data.earned_at || '').slice(0, 10)}
          </p>
          <button
            onClick={share}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-md"
            data-testid="public-badge-share"
          >
            <Share2 className="w-4 h-4" /> Share this badge
          </button>
          <p className="mt-6 text-[10px] text-slate-400">
            Want to earn one too? Join <a href="https://codeittacademy.com" className="text-indigo-600 font-bold hover:underline">CodeITT Academy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
